import type { Decision } from './index';

/**
 * The decisions that get an assessment report (R2).
 *
 * A report is the document an insurer files against a claim, so it exists for an outcome
 * that settles something: repair, replace, or no damage found. The other two do not settle
 * anything — `insufficient_evidence` closes the case on the evidence, and `unable_to_assess` is a
 * machine-only outcome a reviewer may not release at all (R3) — and a PDF asserting either
 * would be a document with no finding in it.
 */
export const REPORTABLE_DECISIONS: readonly Decision[] = ['repair', 'replace', 'no_damage'];

/**
 * Whether a released job has a report behind it.
 *
 * Both consoles ask this, so it is answered once. The alternative is showing the action
 * always and letting `GET /v1/jobs/{id}/report` answer 409 `ERR_NO_REPORT` — which puts an
 * error page in front of someone for pressing a button we offered them.
 *
 * `released` matters as much as the decision: reports are written inside `ReleaseJob`'s
 * transaction, so a held job has none however it is going to end up. A `true` here does
 * not promise the PDF is ready — generation is asynchronous and the endpoint answers 404
 * `ERR_REPORT_NOT_READY` until it is — only that one is owed.
 */
export function hasReport(decision: Decision | undefined, released: boolean): boolean {
  return released && decision !== undefined && REPORTABLE_DECISIONS.includes(decision);
}

/**
 * The CALLER's report route — `Either` auth, for whoever submitted the job.
 *
 * The field console uses this one. Never the reviewer's: `Either` accepts an API key or a
 * submitter's token, and a reviewer holds neither.
 */
export function reportUrl(apiBase: string, jobId: string): string {
  return `${apiBase}/v1/jobs/${jobId}/report`;
}

/**
 * The REVIEWER's report route — `RequireReviewer`, Entra only.
 *
 * Two routes and two names, because the wrong one fails as a 401 that looks like a broken
 * session rather than a wrong address. The review console pointed at `reportUrl` first and
 * would have 401'd every reviewer; the tests asserted the href and never called it, so
 * nothing here caught it.
 */
export function reviewReportUrl(apiBase: string, jobId: string): string {
  return `${apiBase}/v1/review/jobs/${jobId}/report`;
}
